import React, { useState } from 'react';
import { Info, AlertTriangle, CheckCircle2, RotateCcw } from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════
// PILOTE QUALITÉ · M2 · Les principales commandes (REMC C1a)
// Œuvre pédagogique interactive :
//  · Cockpit vue conducteur avec 10 commandes cliquables + hotspots
//  · Chaque hotspot met en surbrillance la commande et affiche sa fiche
//  · Panneau latéral : fonction + main à utiliser + piège classique
//  · Compteur "commandes explorées" avec progression
// ══════════════════════════════════════════════════════════════════════════

const COMMANDES = [
  {
    id: 'volant',
    label: 'Volant',
    x: 245, y: 400, r: 40,
    color: '#8b5cf6',
    fonction: "Diriger le véhicule à gauche ou à droite. Le pivot principal du conducteur.",
    main: "Les DEUX mains à 9h15 (main gauche à 9h, main droite à 3h). Jamais une seule main sur le volant.",
    piege: "Croiser les mains en braquant. Utilise le « tirer-pousser » : la main qui tire fait le travail, l'autre glisse sans traverser.",
  },
  {
    id: 'embrayage',
    label: 'Pédale embrayage',
    x: 175, y: 615, r: 22,
    color: '#f59e0b',
    fonction: "Sépare le moteur de la boîte de vitesses. Sert à démarrer, arrêter et changer de rapport.",
    main: "Pied GAUCHE uniquement. Sur pédale UNIQUEMENT au moment de changer de rapport ou démarrer.",
    piege: "« Rouler avec le pied posé » sur la pédale d'embrayage = patinage constant = usure prématurée.",
  },
  {
    id: 'frein',
    label: 'Pédale de frein',
    x: 215, y: 615, r: 22,
    color: '#dc2626',
    fonction: "Ralentir ou arrêter le véhicule. La pédale la plus importante en sécurité.",
    main: "Pied DROIT. Toujours dessus quand tu approches d'une intersection ou d'un obstacle.",
    piege: "Freiner en appuyant sur les DEUX pieds à la fois. Ou freiner brutalement = perte d'adhérence.",
  },
  {
    id: 'accelerateur',
    label: 'Accélérateur',
    x: 255, y: 615, r: 22,
    color: '#22c55e',
    fonction: "Envoyer de l'essence au moteur pour accélérer. Contrôle la vitesse.",
    main: "Pied DROIT uniquement. Bascule entre frein et accélérateur sans jamais activer les deux.",
    piege: "Accélérations « à fond » puis freinages : consommation +40 %, usure des pneus, inconfort passagers.",
  },
  {
    id: 'levier',
    label: 'Levier de vitesses',
    x: 400, y: 555, r: 25,
    color: '#0ea5e9',
    fonction: "Sélectionne le rapport de la boîte (1, 2, 3, 4, 5, R). Toujours activé pédale embrayage enfoncée.",
    main: "Main DROITE. Position paume-dessus pour rapports pairs (2, 4, R), paume-dessous pour impairs (1, 3, 5).",
    piege: "Vouloir changer de vitesse sans embrayer complètement = grincement + endommagement de la synchro.",
  },
  {
    id: 'frein-main',
    label: 'Frein à main',
    x: 470, y: 555, r: 22,
    color: '#eab308',
    fonction: "Bloque les roues arrière à l'arrêt. Obligatoire au stationnement + secours si frein principal HS.",
    main: "Main DROITE. Toujours activé si tu quittes le véhicule, même 30 secondes.",
    piege: "Oublier de le desserrer avant de démarrer = frein qui chauffe + usure + odeur brûlée.",
  },
  {
    id: 'clignotants',
    label: 'Clignotants',
    x: 140, y: 405, r: 20,
    color: '#fbbf24',
    fonction: "Signaler ton intention de tourner ou changer de file. VITAL en circulation.",
    main: "Main GAUCHE, index sur le commodo. Bas = gauche, haut = droite.",
    piege: "Oublier de mettre le clignotant (surtout en giratoire à la sortie). Ou le laisser après avoir tourné.",
  },
  {
    id: 'contact',
    label: 'Contact / démarreur',
    x: 340, y: 400, r: 18,
    color: '#f97316',
    fonction: "Mise sous tension du véhicule + lancement du moteur (démarreur).",
    main: "Main DROITE. Position 1 = accessoires, 2 = contact (tableau bord allumé), 3 = démarrage (relâcher dès que le moteur tourne).",
    piege: "Continuer à tourner la clé après démarrage = grincement du démarreur = risque de casse.",
  },
  {
    id: 'phares',
    label: 'Commodo feux',
    x: 355, y: 380, r: 18,
    color: '#84cc16',
    fonction: "Allume feux de position, croisement, route, brouillard. À maîtriser en 1 seconde.",
    main: "Main DROITE. Rotation de la bague : O (éteint) → position → croisement.",
    piege: "Rouler en pleins phares en ville = éblouir tous les conducteurs. Route = OK hors agglo si personne en face.",
  },
  {
    id: 'essuie',
    label: 'Essuie-glaces',
    x: 465, y: 380, r: 18,
    color: '#06b6d4',
    fonction: "Nettoyer le pare-brise (pluie, neige, projections).",
    main: "Main DROITE (commodo droit). Vitesse variable + jet lave-glace.",
    piege: "Utiliser les essuie-glaces sur pare-brise SEC = raye le verre. Toujours mouiller avant.",
  },
];

export default function CommandesSvg() {
  const [selected, setSelected] = useState('volant');
  const [explored, setExplored] = useState(new Set(['volant']));

  const current = COMMANDES.find((c) => c.id === selected);

  const select = (id) => {
    setSelected(id);
    setExplored((e) => new Set(e).add(id));
  };
  const reset = () => { setSelected('volant'); setExplored(new Set(['volant'])); };

  return (
    <div className="w-full">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="text-[11px] text-white/60 font-mono-spec">
          <span className="text-white font-bold text-[13px]">{explored.size}</span> / {COMMANDES.length} commandes explorées — clique sur chaque hotspot du cockpit
        </div>
        <button
          type="button"
          onClick={reset}
          data-testid="m2-reset"
          className="ml-auto h-8 px-3 rounded-full border border-white/20 text-white/70 hover:bg-white/5 text-[11px] font-semibold inline-flex items-center gap-1"
        >
          <RotateCcw size={12} /> Reset
        </button>
      </div>

      <div className="grid lg:grid-cols-[1fr,340px] gap-4">
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-b from-[#0f1a2b] to-[#0a1220]">
          <svg viewBox="0 0 800 700" className="w-full h-auto block max-h-[48vh] sm:max-h-[52vh]" role="img" aria-label="Cockpit et commandes principales">
            <defs>
              <linearGradient id="m2-dash" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1e293b" />
                <stop offset="100%" stopColor="#0b1120" />
              </linearGradient>
              <linearGradient id="m2-wheel" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3f3f46" />
                <stop offset="100%" stopColor="#18181b" />
              </linearGradient>
              <radialGradient id="m2-halo">
                <stop offset="0%" stopColor={current.color} stopOpacity="0.55" />
                <stop offset="60%" stopColor={current.color} stopOpacity="0.15" />
                <stop offset="100%" stopColor={current.color} stopOpacity="0" />
              </radialGradient>
            </defs>

            {/* Fond & pare-brise */}
            <rect width={800} height={700} fill="#0b1120" />
            <path d="M 0 0 L 800 0 L 780 180 L 20 180 Z" fill="#1e3a5f" opacity={0.7} />
            <path d="M 50 60 L 200 40 L 600 40 L 750 60 L 720 160 L 80 160 Z" fill="#3b5f80" opacity={0.5} />

            {/* Tableau de bord */}
            <path d="M 0 180 L 800 180 L 800 300 L 0 300 Z" fill="url(#m2-dash)" />
            <path d="M 0 300 L 800 300 L 800 320 L 0 320 Z" fill="#0b1120" />

            {/* Compteurs */}
            <g transform="translate(160 240)">
              <circle r={38} fill="#0b1120" stroke="#334155" strokeWidth={2} />
              <circle r={30} fill="none" stroke="#22c55e" strokeWidth={2} strokeDasharray="4 3" />
              <text y={4} textAnchor="middle" fill="#94a3b8" fontSize={9} fontFamily="monospace">km/h</text>
            </g>
            <g transform="translate(290 240)">
              <circle r={38} fill="#0b1120" stroke="#334155" strokeWidth={2} />
              <circle r={30} fill="none" stroke="#fbbf24" strokeWidth={2} strokeDasharray="4 3" />
              <text y={4} textAnchor="middle" fill="#94a3b8" fontSize={9} fontFamily="monospace">tr/min</text>
            </g>

            {/* Halo autour de la commande sélectionnée */}
            <circle cx={current.x} cy={current.y} r={current.r * 2.2} fill="url(#m2-halo)">
              <animate attributeName="opacity" values="0.6;1;0.6" dur="1.8s" repeatCount="indefinite" />
            </circle>

            {/* VOLANT */}
            <g transform={`translate(245 400)`}>
              <ellipse cx={0} cy={0} rx={95} ry={70} fill="none" stroke="url(#m2-wheel)" strokeWidth={16} />
              <ellipse cx={0} cy={0} rx={95} ry={70} fill="none" stroke="#71717a" strokeWidth={2} />
              <ellipse cx={0} cy={0} rx={26} ry={20} fill="#18181b" stroke="#71717a" strokeWidth={2} />
              <text y={4} textAnchor="middle" fill="#71717a" fontSize={12} fontWeight="900" fontFamily="serif">CV</text>
              <line x1={-26} y1={0} x2={-95} y2={0} stroke="#3f3f46" strokeWidth={5} />
              <line x1={26} y1={0} x2={95} y2={0} stroke="#3f3f46" strokeWidth={5} />
              <line x1={0} y1={20} x2={0} y2={70} stroke="#3f3f46" strokeWidth={5} />
              {/* Commodo gauche (clignotants) */}
              <g transform="translate(-105 5)">
                <rect x={-30} y={-4} width={30} height={8} rx={4} fill="#3f3f46" stroke="#0b1120" strokeWidth={1} />
                <circle cx={-33} cy={0} r={4} fill="#fbbf24" opacity={0.6} />
              </g>
              {/* Commodo droit (phares/essuie-glace) */}
              <g transform="translate(105 -20)">
                <rect x={0} y={-4} width={30} height={8} rx={4} fill="#3f3f46" stroke="#0b1120" strokeWidth={1} />
              </g>
              <g transform="translate(115 -20)">
                <rect x={0} y={-4} width={30} height={8} rx={4} fill="#3f3f46" stroke="#0b1120" strokeWidth={1} />
              </g>
            </g>

            {/* Contact clef */}
            <g transform="translate(340 400)">
              <circle r={12} fill="#1e293b" stroke="#71717a" strokeWidth={2} />
              <rect x={-2} y={-14} width={4} height={12} fill="#71717a" />
              <text y={30} textAnchor="middle" fill="#94a3b8" fontSize={8} fontFamily="monospace">CONTACT</text>
            </g>

            {/* Levier vitesses (console centrale) */}
            <g transform="translate(400 555)">
              <ellipse cx={0} cy={20} rx={35} ry={12} fill="#1e293b" stroke="#0b1120" strokeWidth={1.5} />
              <line x1={0} y1={0} x2={0} y2={20} stroke="#71717a" strokeWidth={5} />
              <circle r={12} fill="#71717a" stroke="#0b1120" strokeWidth={1.5} />
              <text y={4} textAnchor="middle" fill="#0b1120" fontSize={10} fontWeight="900">3</text>
              <text x={-30} y={45} fill="#94a3b8" fontSize={8} fontFamily="monospace">1 3 5</text>
              <text x={-30} y={55} fill="#94a3b8" fontSize={8} fontFamily="monospace">2 4 R</text>
            </g>

            {/* Frein à main (droite du levier) */}
            <g transform="translate(470 555)">
              <rect x={-6} y={-6} width={12} height={38} rx={4} fill="#374151" stroke="#0b1120" strokeWidth={1.5} />
              <rect x={-14} y={-16} width={28} height={12} rx={4} fill="#525b6b" stroke="#0b1120" strokeWidth={1.5} />
              <rect x={-2} y={-14} width={4} height={8} fill="#dc2626" />
              <text x={0} y={50} textAnchor="middle" fill="#94a3b8" fontSize={8} fontFamily="monospace">FREIN À MAIN</text>
            </g>

            {/* Pédalier */}
            <g transform="translate(175 615)">
              <rect x={-15} y={-14} width={22} height={28} rx={4} fill="#374151" stroke="#0b1120" strokeWidth={1.5} />
              <text x={-4} y={4} textAnchor="middle" fill="#94a3b8" fontSize={8} fontFamily="monospace">C</text>
            </g>
            <g transform="translate(215 615)">
              <rect x={-15} y={-14} width={26} height={28} rx={4} fill="#374151" stroke="#0b1120" strokeWidth={1.5} />
              <text x={-2} y={4} textAnchor="middle" fill="#94a3b8" fontSize={8} fontFamily="monospace">F</text>
            </g>
            <g transform="translate(255 615)">
              <rect x={-11} y={-14} width={22} height={28} rx={4} fill="#374151" stroke="#0b1120" strokeWidth={1.5} />
              <text x={0} y={4} textAnchor="middle" fill="#94a3b8" fontSize={8} fontFamily="monospace">A</text>
            </g>

            {/* HOTSPOTS cliquables */}
            {COMMANDES.map((cmd) => {
              const isSel = selected === cmd.id;
              const isExplored = explored.has(cmd.id);
              return (
                <g
                  key={cmd.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => select(cmd.id)}
                  data-testid={`m2-hotspot-${cmd.id}`}
                >
                  <circle
                    cx={cmd.x} cy={cmd.y} r={cmd.r * 0.65}
                    fill={cmd.color}
                    fillOpacity={isSel ? 0.28 : (isExplored ? 0.15 : 0)}
                    stroke={cmd.color}
                    strokeWidth={isSel ? 3 : 2}
                    strokeDasharray={isSel ? '' : '4 4'}
                    opacity={isSel ? 1 : 0.85}
                  />
                  {isExplored && !isSel && (
                    <circle cx={cmd.x + cmd.r * 0.5} cy={cmd.y - cmd.r * 0.5} r={6} fill={cmd.color} stroke="#0b1120" strokeWidth={1.5}>
                      {/* Check */}
                    </circle>
                  )}
                  {isSel && (
                    <text x={cmd.x} y={cmd.y + cmd.r + 18} textAnchor="middle" fill={cmd.color} fontSize={10.5} fontWeight="bold" fontFamily="monospace">
                      {cmd.label}
                    </text>
                  )}
                </g>
              );
            })}

            {/* Bandeau bas */}
            <g transform="translate(0, 655)">
              <rect width={800} height={45} fill="#000" opacity={0.65} />
              <text x={20} y={28} fill="#fff" fontSize={13} fontWeight="bold">M2 · Les principales commandes</text>
              <text x={20} y={42} fill="#94a3b8" fontSize={11}>REMC C1a — Connaître les commandes du véhicule</text>
              <circle cx={770} cy={22} r={16} fill="#dc2626" />
              <text x={770} y={27} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="900">CV</text>
            </g>
          </svg>

          {/* Progression */}
          <div className="p-3.5 border-t border-white/10 bg-black/40">
            <div className="w-full h-2.5 rounded-full bg-black/60 border border-white/10 overflow-hidden">
              <div
                className="h-full transition-all duration-500"
                style={{
                  width: `${(explored.size / COMMANDES.length) * 100}%`,
                  background: explored.size === COMMANDES.length ? '#22c55e' : '#f59e0b',
                }}
              />
            </div>
          </div>
        </div>

        {/* Panneau */}
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-5 self-start lg:max-h-[calc(100vh-300px)] lg:overflow-y-auto lg:custom-scroll" data-testid="m2-panel">
          <div className="rounded-xl border-2 p-3.5" style={{ borderColor: current.color, background: current.color + '18' }}>
            <div className="text-[10.5px] font-mono-spec uppercase tracking-wider font-bold" style={{ color: current.color }}>
              Commande sélectionnée
            </div>
            <div className="mt-1 font-serif-editorial text-white text-[20px] leading-tight">
              {current.label}
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-cyan-500/30 bg-cyan-950/20 p-3 flex items-start gap-2">
            <Info size={14} className="text-cyan-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-cyan-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Fonction</div>
              <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">{current.fonction}</p>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-purple-500/40 bg-purple-950/20 p-3 flex items-start gap-2">
            <span className="text-purple-300 text-[14px] mt-0.5 shrink-0">✋</span>
            <div>
              <div className="text-purple-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Main / pied</div>
              <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">{current.main}</p>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-950/20 p-3 flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">⚠ Piège classique</div>
              <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">{current.piege}</p>
            </div>
          </div>

          {explored.size === COMMANDES.length && (
            <div className="mt-3 rounded-lg border border-emerald-500/60 bg-emerald-950/30 p-3 text-center">
              <CheckCircle2 size={20} className="text-emerald-300 inline-block mb-1" />
              <p className="text-white text-[13px] font-semibold">Tu maîtrises tes 10 commandes 🎉</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
